/* =========================================================================
   Local inventory (v1.1.0 reward vocabulary, client side). A tiny counter store
   at localStorage['catholics.inventory'] mapping ItemType → quantity. Everyone
   (guests + accounts) keeps a local copy so the wheel's item slots, the relic
   quest, and the Journey Director's arcade-ticket count all work fully offline;
   accounts additionally reconcile with the server inventory on sign-in.

   House pattern: pure reducers (grantReducer/spendReducer) are unit-tested; the
   storage-backed grant/spend wrap them over an injectable KVStore seam (mirrors
   dailyBlessing.ts) and emit InventoryGranted / InventorySpent telemetry — the
   SAME events the quest engine and dashboards consume.
   ========================================================================= */
import { track } from './telemetry';
import type { KVStore } from './dailyBlessing';

const KEY = 'catholics.inventory';

/** Item types that exist client-side (mirror app.InventoryItems, db/015). */
export type ItemType = 'relic_token' | 'rosary_bead' | 'arcade_ticket' | 'pilgrim_token' | 'streak_shield';

export type InventoryMap = Record<string, number>;

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/* ---------------------------- pure reducers ---------------------------- */

/** Add `qty` (>=0) of `type`, returning a NEW map (never mutates the input). */
export function grantReducer(map: InventoryMap, type: string, qty: number): InventoryMap {
  const add = Math.max(0, Math.floor(qty));
  return { ...map, [type]: (map[type] || 0) + add };
}

/** Attempt to remove `qty` of `type`. Guarded: returns ok:false (map unchanged)
    when the player doesn't hold enough. */
export function spendReducer(map: InventoryMap, type: string, qty: number): { map: InventoryMap; ok: boolean } {
  const take = Math.max(0, Math.floor(qty));
  const have = map[type] || 0;
  if (take === 0) return { map, ok: true };
  if (have < take) return { map, ok: false };
  return { map: { ...map, [type]: have - take }, ok: true };
}

/* --------------------------- storage-backed --------------------------- */

export function readInventory(store: KVStore | null = deviceStore()): InventoryMap {
  try {
    if (!store) return {};
    const raw = store.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') return parsed as InventoryMap;
    return {};
  } catch {
    return {};
  }
}

function writeInventory(map: InventoryMap, store: KVStore | null): void {
  try {
    if (store) store.setItem(KEY, JSON.stringify(map));
  } catch {
    /* private mode / eviction — inventory simply won't persist */
  }
}

export function getQty(type: string, store: KVStore | null = deviceStore()): number {
  return readInventory(store)[type] || 0;
}

/** Grant items locally + emit InventoryGranted{type,qty,source}. `source`
    describes where it came from ('wheel', 'quest', …) for the funnel. */
export function grantItem(
  type: string,
  qty: number,
  source: string,
  store: KVStore | null = deviceStore(),
): void {
  const add = Math.max(0, Math.floor(qty));
  if (add === 0) return;
  writeInventory(grantReducer(readInventory(store), type, add), store);
  track('InventoryGranted', { type, qty: add, source });
}

/** Spend items locally + emit InventorySpent{type,qty,sink}. Returns false
    (nothing spent, no event) when the player lacks the quantity. */
export function spendItem(
  type: string,
  qty: number,
  sink: string,
  store: KVStore | null = deviceStore(),
): boolean {
  const take = Math.max(0, Math.floor(qty));
  const { map, ok } = spendReducer(readInventory(store), type, take);
  if (!ok) return false;
  writeInventory(map, store);
  if (take > 0) track('InventorySpent', { type, qty: take, sink });
  return true;
}
